import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Loader2, RefreshCw, Search, Send } from "lucide-react";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Badge } from "../ui/badge";
import TemplateMessage from "../TemplateMessage";
import { cn } from "../../lib/utils";
import api from "../../services/api";
import toastError from "../../errors/toastError";

// Escolher e mandar um modelo aprovado.
//
// É o único caminho quando a janela de 24 h fechou: passado esse prazo a Meta
// recusa texto livre com o erro 131047, e a mensagem chega a ser gravada aqui
// como enviada — o atendente acha que falou e o cliente nunca recebeu.

const ROTULO_CATEGORIA = {
  MARKETING: "Marketing",
  UTILITY: "Utilitário",
  AUTHENTICATION: "Autenticação",
  SERVICE: "Atendimento"
};

// Quantas variáveis o corpo do modelo pede: {{1}}, {{2}}…
const contarVariaveis = (componentes = []) => {
  const corpo = componentes.find(
    (c) => String(c?.type).toUpperCase() === "BODY"
  );

  const achadas = String(corpo?.text || "").match(/\{\{\s*\d+\s*\}\}/g) || [];

  return new Set(achadas.map((v) => v.replace(/\D/g, ""))).size;
};

// A prévia do que o cliente vai receber, no mesmo desenho da conversa.
const previaDoTemplate = (template, valores) => {
  const componentes = template.components || [];

  const acharTipo = (tipo) =>
    componentes.find((c) => String(c?.type).toUpperCase() === tipo);

  const preencher = (texto) =>
    String(texto || "").replace(/\{\{\s*(\d+)\s*\}\}/g, (todo, indice) => {
      const valor = valores[Number(indice) - 1];

      return valor ? valor : todo;
    });

  const cabecalho = acharTipo("HEADER");
  const botoes = acharTipo("BUTTONS");

  return {
    tipo: "whatsapp",
    nome: template.name,
    categoria: template.category,
    cabecalho: cabecalho
      ? {
          tipo: String(cabecalho.format || "TEXT").toLowerCase(),
          texto: preencher(cabecalho.text)
        }
      : null,
    corpo: preencher(acharTipo("BODY")?.text),
    rodape: acharTipo("FOOTER")?.text || null,
    botoes: (botoes?.buttons || []).map((b) => ({
      tipo: String(b.type || "").toLowerCase(),
      texto: b.text,
      url: b.url,
      telefone: b.phone_number
    }))
  };
};

const EnviarTemplateModal = ({ open, onClose, ticketId, onEnviado }) => {
  const [templates, setTemplates] = useState([]);
  const [busca, setBusca] = useState("");
  const [escolhido, setEscolhido] = useState(null);
  const [valores, setValores] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [enviando, setEnviando] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);

    try {
      const { data } = await api.get("/meta/templates");
      setTemplates(data || []);
    } catch (err) {
      toastError(err);
    }

    setCarregando(false);
  }, []);

  useEffect(() => {
    if (!open) return;

    setEscolhido(null);
    setValores([]);
    setBusca("");
    carregar();
  }, [open, carregar]);

  // Só modelo aprovado pode ser enviado; mostrar os outros com o motivo evita
  // a pergunta "cadê o modelo que eu criei ontem".
  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();

    return templates.filter(
      (t) => !termo || String(t.name).toLowerCase().includes(termo)
    );
  }, [templates, busca]);

  const sincronizar = async () => {
    setSincronizando(true);

    try {
      const { data } = await api.post("/meta/templates/sync");
      toast.success(`${data.total} modelos sincronizados da Meta.`);
      await carregar();
    } catch (err) {
      toastError(err);
    }

    setSincronizando(false);
  };

  const escolher = (template) => {
    setEscolhido(template);
    setValores(Array(contarVariaveis(template.components)).fill(""));
  };

  const enviar = async () => {
    if (valores.some((v) => !String(v).trim())) {
      toast.error("Preencha todas as variáveis do modelo.");
      return;
    }

    setEnviando(true);

    try {
      await api.post(`/meta/templates/${escolhido.id}/send`, {
        ticketId,
        variaveis: valores
      });

      toast.success("Modelo enviado. A conversa reabre por 24 h.");
      await onEnviado?.();
      onClose();
    } catch (err) {
      toastError(err);
    }

    setEnviando(false);
  };

  return (
    <Dialog open={open} onOpenChange={(aberto) => !aberto && onClose()}>
      <DialogContent className="flex max-h-[85vh] max-w-2xl flex-col">
        <DialogHeader>
          <DialogTitle>Enviar modelo</DialogTitle>
          <DialogDescription>
            Passadas 24 h da última mensagem do cliente, o WhatsApp só aceita
            modelo aprovado. Enviar um reabre a conversa por mais 24 h.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              className="pl-8"
              placeholder="Procurar modelo"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
            />
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={sincronizar}
            disabled={sincronizando}
          >
            {sincronizando ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            Buscar da Meta
          </Button>
        </div>

        {carregando ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : templates.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            Nenhum modelo cadastrado. Use “Buscar da Meta” para trazer os que
            já existem no WhatsApp Business.
          </p>
        ) : (
          <div className="grid flex-1 gap-3 overflow-hidden sm:grid-cols-2">
            <div className="-mx-1 space-y-1 overflow-y-auto px-1">
              {visiveis.map((template) => {
                const aprovado = template.status === "APPROVED";

                return (
                  <button
                    key={template.id}
                    type="button"
                    disabled={!aprovado}
                    onClick={() => escolher(template)}
                    className={cn(
                      "w-full rounded-lg border p-2.5 text-left transition-colors",
                      escolhido?.id === template.id
                        ? "border-primary bg-primary/5"
                        : "hover:bg-accent",
                      !aprovado && "cursor-not-allowed opacity-50"
                    )}
                  >
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="truncate text-sm font-medium">
                        {template.name}
                      </span>
                      <Badge variant="secondary" className="text-[10px]">
                        {ROTULO_CATEGORIA[template.category] ||
                          template.category}
                      </Badge>
                      <span className="text-[10px] uppercase text-muted-foreground">
                        {template.language}
                      </span>
                    </div>

                    {!aprovado && (
                      <p className="mt-0.5 text-xs text-amber-600 dark:text-amber-400">
                        {template.status === "PENDING"
                          ? "Aguardando aprovação da Meta"
                          : `Não pode ser enviado (${template.status})`}
                      </p>
                    )}
                  </button>
                );
              })}
            </div>

            <div className="-mx-1 space-y-3 overflow-y-auto px-1">
              {escolhido ? (
                <>
                  {valores.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-xs">
                        Variáveis do modelo
                      </Label>
                      {valores.map((valor, indice) => (
                        // eslint-disable-next-line react/no-array-index-key
                        <div key={indice} className="space-y-1">
                          <Label
                            htmlFor={`variavel-${indice}`}
                            className="text-xs text-muted-foreground"
                          >
                            {`{{${indice + 1}}}`}
                          </Label>
                          <Input
                            id={`variavel-${indice}`}
                            value={valor}
                            onChange={(e) =>
                              setValores((atuais) =>
                                atuais.map((v, i) =>
                                  i === indice ? e.target.value : v
                                )
                              )
                            }
                          />
                        </div>
                      ))}
                    </div>
                  )}

                  <div>
                    <Label className="text-xs">O cliente vai ver</Label>
                    <div className="mt-1.5">
                      <TemplateMessage
                        dados={previaDoTemplate(escolhido, valores)}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <p className="py-8 text-center text-sm text-muted-foreground">
                  Escolha um modelo à esquerda para ver a prévia.
                </p>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={enviando}>
            Cancelar
          </Button>
          <Button onClick={enviar} disabled={!escolhido || enviando}>
            {enviando ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
            Enviar modelo
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EnviarTemplateModal;
